import React, { useState } from 'react';
import { useDataProvider, useNotify } from 'react-admin';
import { useTiptapEditor } from 'ra-input-rich-text';
import { DOMSerializer, DOMParser as ProseMirrorDOMParser } from '@tiptap/pm/model';
import { Divider, Tooltip, ToggleButton, ToggleButtonGroup, CircularProgress } from '@mui/material';
import SpellcheckIcon from '@mui/icons-material/Spellcheck';
import FeatureIntro from '../FeatureIntro';
import AutoFixHighIcon from '@mui/icons-material/AutoFixHigh';
import VerticalAlignCenterIcon from '@mui/icons-material/VerticalAlignCenter';
import DrawIcon from '@mui/icons-material/Draw';

interface AiAction {
  id: string;
  label: string;
  icon: React.ReactNode;
  action: string;
}

type AiActionItem = AiAction | AiAction[];

const AI_ACTIONS: AiActionItem[] = [
  [
    { id: 'correct', label: 'Auto correção com IA', icon: <SpellcheckIcon fontSize="inherit" />, action: 'correct' },
    { id: 'rephrase', label: 'Refrasear com IA', icon: <AutoFixHighIcon fontSize="inherit" />, action: 'rephrase' },
    { id: 'shorten', label: 'Encurtar com IA', icon: <VerticalAlignCenterIcon fontSize="inherit" />, action: 'shorten' },
  ],
  { id: 'continue', label: 'Continuar escrevendo com IA', icon: <DrawIcon fontSize="inherit" />, action: 'continue' },
];

const stripImages = (html: string): { cleanHtml: string; images: string[] } => {
  const images: string[] = [];
  let index = 0;
  const cleanHtml = html.replace(/<img[^>]*>/gi, (match) => {
    images.push(match);
    return `<span data-img-ref="${index++}"></span>`;
  });
  return { cleanHtml, images };
};

const restoreImages = (html: string, images: string[]): string => {
  return html.replace(/<span data-img-ref="(\d+)">\s*<\/span>/gi, (_, idx) => {
    return images[parseInt(idx)] || '';
  });
};


const AiToolbar = ({ size, fieldType }: { size?: 'small' | 'medium' | 'large'; fieldType?: string }) => {
  const editor = useTiptapEditor();
  const dataProvider = useDataProvider();
  const notify = useNotify();
  const [loadingAction, setLoadingAction] = useState<string | null>(null);
  const [firstIntroDismissed, setFirstIntroDismissed] = useState(() => {
    try {
      const dismissed = JSON.parse(localStorage.getItem('pontuax_feature_intros') || '[]');
      return dismissed.includes('ai-toolbar-edit-tools');
    } catch { return false; }
  });

  const handleAction = async (aiAction: AiAction) => {
    if (!editor || loadingAction) return;

    const { from, to } = editor.state.selection;
    // Check if selection has actual text (not just an image node)
    const selectedText = editor.state.doc.textBetween(from, to, ' ').trim();
    const hasSelection = from !== to && selectedText.length > 0;

    let textToProcess: string;

    if (hasSelection) {
      // Get HTML of selected text using ProseMirror's DOMSerializer
      const slice = editor.state.selection.content();
      const serializer = DOMSerializer.fromSchema(editor.schema);
      const tempDiv = document.createElement('div');
      const dom = serializer.serializeFragment(slice.content);
      tempDiv.appendChild(dom);
      textToProcess = tempDiv.innerHTML;
    } else {
      textToProcess = editor.getHTML();
    }

    // Strip images from HTML before sending to LLM
    const { cleanHtml, images } = stripImages(textToProcess);

    if (!cleanHtml || cleanHtml.trim().length === 0) {
      notify('Não há texto para processar.', { type: 'warning' });
      return;
    }

    setLoadingAction(aiAction.id);

    try {
      const response = await dataProvider.create('chains/chainId/ai-tools/text-editor', {
        data: { text: cleanHtml, action: aiAction.action, fieldType },
      });

      const result = response.data as any;

      if (result.success && result.result) {
        // Restore images in the corrected HTML
        const finalHtml = restoreImages(result.result, images);

        if (aiAction.action === 'continue') {
          // Append generated text at the end of the editor
          editor
            .chain()
            .focus('end')
            .insertContent(finalHtml)
            .run();
        } else if (hasSelection) {
          // Strip wrapping <p> tags to avoid line breaks in inline selections
          let correctedText = finalHtml.trim();
          const singleParagraphMatch = correctedText.match(/^<p>(.*)<\/p>$/s);
          if (singleParagraphMatch) {
            correctedText = singleParagraphMatch[1];
          }

          editor
            .chain()
            .focus()
            .insertContentAt({ from, to }, correctedText)
            .run();
        } else {
          // Parse corrected HTML into ProseMirror nodes
          const parser = ProseMirrorDOMParser.fromSchema(editor.schema);
          const tempDiv = document.createElement('div');
          tempDiv.innerHTML = finalHtml;
          const newDoc = parser.parse(tempDiv);

          const tr = editor.state.tr.replaceWith(
            0,
            editor.state.doc.content.size,
            newDoc.content
          );
          editor.view.dispatch(tr);
        }

        const successMessages: Record<string, string> = {
          correct: 'Texto corrigido com sucesso!',
          rephrase: 'Texto refraseado com sucesso!',
          shorten: 'Texto encurtado com sucesso!',
          continue: 'Texto continuado com sucesso!',
        };
        notify(successMessages[aiAction.action] || 'Texto processado com sucesso!', { type: 'success' });
      } else {
        notify(result.error || 'Erro ao processar texto.', { type: 'error' });
      }
    } catch (error) {
      console.error('[AiToolbar] Error:', error);
      notify('Erro ao processar texto. Tente novamente.', { type: 'error' });
    } finally {
      setLoadingAction(null);
    }
  };

  const renderButton = (aiAction: AiAction) => (
    <Tooltip key={aiAction.id} title={aiAction.label}>
      <ToggleButton
        value={aiAction.id}
        onClick={() => handleAction(aiAction)}
        disabled={!editor?.isEditable || loadingAction !== null}
        sx={{
          color: '#667eea',
          '&:hover': { bgcolor: 'rgba(102, 126, 234, 0.08)' },
        }}
      >
        {loadingAction === aiAction.id ? (
          <CircularProgress size={16} color="inherit" />
        ) : (
          aiAction.icon
        )}
      </ToggleButton>
    </Tooltip>
  );

  return (
    <>
      <Divider orientation="vertical" flexItem sx={{ mx: 0.5 }} />
      <FeatureIntro
        id="ai-toolbar-edit-tools"
        title="✨ Ferramentas de edição com IA"
        description="Corrija erros ortográficos, reescreva de forma diferente ou encurte o texto. Selecione um trecho do texto para aplicar a ação apenas na seleção."
        delay={600}
        onDismiss={() => setFirstIntroDismissed(true)}
      >
        <ToggleButtonGroup size={size}>
          {(AI_ACTIONS[0] as AiAction[]).map(renderButton)}
        </ToggleButtonGroup>
      </FeatureIntro>
      <FeatureIntro
        id="ai-toolbar-continue"
        title="✨ Continuar escrevendo com IA"
        description="A IA continua o texto a partir do ponto atual, adicionando frases que complementam naturalmente o conteúdo no contexto da campanha."
        delay={800}
        disabled={!firstIntroDismissed}
      >
        {renderButton(AI_ACTIONS[1] as AiAction)}
      </FeatureIntro>
    </>
  );
};

export default AiToolbar;
